import { useCallback, useEffect, useRef, useState } from 'react';
import type { CmsAgentRequest, ConversationDetail } from '@ctd/agent-contract';
import { useStore } from '~contexts/mobx';
import { drainSseEvents } from '../utils/sse';
import {
    applyAgentEvent,
    emptyThread,
    failTurn,
    forgetConversation,
    startTurn,
    stopTurn,
    threadFromConversation,
} from '../utils/agent-thread-state';
import type { AgentThread } from '../utils/agent-thread-state';

const API_BASE = import.meta.env.REACT_APP_API_URL;

export interface UseAgentStream {
    thread: AgentThread;
    isStreaming: boolean;
    ask: (question: string) => void;
    /** Cancels the turn in flight, which then reads as stopped. */
    stop: () => void;
    /** Cancels the turn in flight and empties the thread; the next question opens a conversation. */
    newConversation: () => void;
    /** Cancels the turn in flight and shows a stored conversation; the next question continues it. */
    loadConversation: (detail: ConversationDetail) => void;
}

export function useAgentStream(
    fetchImpl: typeof fetch = fetch
): UseAgentStream {
    const rootStore = useStore();
    const [thread, setThread] = useState<AgentThread>(emptyThread);
    const [isStreaming, setIsStreaming] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const { conversationId } = thread;

    const cancel = useCallback((): boolean => {
        if (!abortRef.current) {
            return false;
        }
        abortRef.current.abort();
        abortRef.current = null;
        setIsStreaming(false);
        return true;
    }, []);

    const stop = useCallback(() => {
        if (cancel()) {
            setThread(stopTurn);
        }
    }, [cancel]);

    const newConversation = useCallback(() => {
        cancel();
        setThread(emptyThread());
    }, [cancel]);

    const loadConversation = useCallback(
        (detail: ConversationDetail) => {
            cancel();
            setThread(threadFromConversation(detail));
        },
        [cancel]
    );

    const ask = useCallback(
        async (question: string) => {
            const message = question.trim();
            if (!message) {
                return;
            }
            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setThread((prev) => startTurn(prev, message));
            setIsStreaming(true);

            const isCurrent = () => abortRef.current === controller;
            const post = (id: string | null) => {
                const request: CmsAgentRequest = id
                    ? { conversationId: id, message }
                    : { message };
                return fetchImpl(`${API_BASE}/api-cms/agent/stream`, {
                    method: 'POST',
                    // Reuse the store's standard headers (jwt + accept-language) so the
                    // agent request is built like every other API call.
                    headers: rootStore.getHeaders({
                        'Content-Type': 'application/json',
                    }),
                    body: JSON.stringify(request),
                    signal: controller.signal,
                });
            };

            try {
                let response = await post(conversationId);
                // The stored conversation is gone (purged): the question opens a new one.
                if (response.status === 404 && conversationId) {
                    if (!isCurrent()) {
                        return;
                    }
                    setThread(forgetConversation);
                    response = await post(null);
                }

                if (!response.ok || !response.body) {
                    if (isCurrent()) {
                        setThread(failTurn);
                        if (response.status === 401) {
                            rootStore.handleUnauthorized();
                        }
                    }
                    return;
                }

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                for (;;) {
                    const { done, value } = await reader.read();
                    if (done) {
                        break;
                    }
                    buffer += decoder.decode(value, { stream: true });
                    const drained = drainSseEvents(buffer);
                    buffer = drained.rest;
                    for (const event of drained.events) {
                        if (!isCurrent()) {
                            return;
                        }
                        setThread((prev) => applyAgentEvent(prev, event));
                    }
                }
                // A stream that closes before `done` leaves the turn unfinished.
                if (isCurrent()) {
                    setThread(failTurn);
                }
            } catch (error) {
                if ((error as { name?: string }).name === 'AbortError') {
                    return;
                }
                if (isCurrent()) {
                    setThread(failTurn);
                }
            } finally {
                // Only the latest turn settles the UI; a superseded (aborted) turn must
                // not flip isStreaming off or null the ref while the newer turn runs.
                if (isCurrent()) {
                    abortRef.current = null;
                    setIsStreaming(false);
                }
            }
        },
        [conversationId, fetchImpl, rootStore]
    );

    useEffect(() => stop, [stop]); // abort on unmount

    return {
        thread,
        isStreaming,
        ask,
        stop,
        newConversation,
        loadConversation,
    };
}
